import React, { useState, useEffect, useMemo } from 'react';
import { Users, X } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { getPlayers, getPlayerPillarScores } from '../api';
import { PLAYER_COLORS } from './gps/InMatchAnalysis';
import { PILLAR_LABELS } from '../components/PillarBubbleSpider';
import PageChrome from '../components/PageChrome';
import PlayerDetailPanel from '../components/PlayerDetailPanel';

const PILLAR_KEYS = ['physical', 'technical', 'tactical', 'identity', 'availability'];
const MAX_COMPARE = 4; // + the primary player = 5, same cap as Comparison Builder's MAX_PLAYERS

function normalizePillars(row) {
  const n = v => (v === null || v === undefined ? null : Number(v));
  return {
    physical: n(row?.physical), technical: n(row?.technical), tactical: n(row?.tactical),
    identity: n(row?.identity), availability: n(row?.availability),
  };
}

// Comparison mode's own multi-select, adapted from ComparisonBuilder.js's PlayerMultiSelect
// (same interaction pattern: dropdown checklist, capped selection) — that component isn't
// exported for reuse, and its data plumbing is drill-metric-specific, so this is a fresh,
// smaller copy for picking comparison players rather than a shared import.
function ComparePlayerPicker({ players, selected, onChange, max, excludeId }) {
  const [open, setOpen] = useState(false);
  const candidates = players.filter(p => p.id !== excludeId);
  const toggle = id => {
    const idStr = String(id);
    if (selected.includes(idStr)) onChange(selected.filter(s => s !== idStr));
    else if (selected.length < max) onChange([...selected, idStr]);
  };
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13 }}>
        <Users size={14} /> {selected.length ? `Comparing ${selected.length}` : 'Add players to compare'}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50, background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 10, minWidth: 220, maxHeight: 280, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}
          onMouseLeave={() => setOpen(false)}>
          <div style={{ padding: '6px 12px', fontSize: 11, color: 'var(--text-dimmed)' }}>Up to {max} players</div>
          {candidates.map(p => {
            const idStr = String(p.id);
            const checked = selected.includes(idStr);
            const disabled = !checked && selected.length >= max;
            return (
              <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', cursor: disabled ? 'default' : 'pointer', fontSize: 13, color: disabled ? 'var(--text-dimmed)' : 'var(--text-primary)' }}>
                <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggle(p.id)} />
                {p.name}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function IndividualReport({ user, onNavigate }) {
  const [players, setPlayers] = useState([]);
  const [playerId, setPlayerId] = useState('');
  const [period, setPeriod] = useState('week');
  const [primaryScore, setPrimaryScore] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [compareScores, setCompareScores] = useState({}); // playerId -> pillars

  useEffect(() => {
    getPlayers().then(list => {
      const active = (list || []).filter(p => p.active !== false).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setPlayers(active);
      if (active.length) setPlayerId(String(active[0].id));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!playerId) return;
    getPlayerPillarScores(playerId, period).then(row => setPrimaryScore(row)).catch(() => setPrimaryScore(null));
  }, [playerId, period]);

  useEffect(() => {
    if (!compareIds.length) { setCompareScores({}); return; }
    Promise.all(compareIds.map(id => getPlayerPillarScores(id, period).then(row => [id, row]).catch(() => [id, null])))
      .then(entries => setCompareScores(Object.fromEntries(entries)));
  }, [compareIds, period]);

  const player = players.find(p => String(p.id) === String(playerId));
  const pillars = useMemo(() => normalizePillars(primaryScore), [primaryScore]);

  const comparisonPlayers = useMemo(() => {
    const primary = player ? { id: player.id, name: player.name, pillars } : null;
    const others = compareIds.map(id => {
      const p = players.find(pl => String(pl.id) === String(id));
      return { id, name: p?.name || `#${id}`, pillars: normalizePillars(compareScores[id]) };
    });
    return primary ? [primary, ...others] : others;
  }, [player, pillars, compareIds, compareScores, players]);

  const showComparison = compareIds.length > 0;

  const comparisonRadarData = useMemo(() => PILLAR_KEYS.map(k => {
    const row = { pillar: PILLAR_LABELS[k] };
    comparisonPlayers.forEach(p => { row[p.name] = p.pillars[k] == null ? 0 : p.pillars[k]; });
    return row;
  }), [comparisonPlayers]);
  const maxRadarValue = Math.max(100, ...comparisonPlayers.flatMap(p => PILLAR_KEYS.map(k => p.pillars[k] || 0)));

  return (
    <PageChrome onNavigate={onNavigate} user={user}>
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 16px 40px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginBottom: 8 }}>
        <select value={playerId} onChange={e => setPlayerId(e.target.value)}
          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13 }}>
          {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <ComparePlayerPicker players={players} selected={compareIds} onChange={setCompareIds} max={MAX_COMPARE} excludeId={player?.id} />
      </div>

      {showComparison && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginBottom: 16 }}>
          {compareIds.map((id, i) => {
            const p = players.find(pl => String(pl.id) === String(id));
            return (
              <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 99, background: 'var(--bg-card)', border: `1px solid ${PLAYER_COLORS[(i + 1) % PLAYER_COLORS.length]}`, fontSize: 12, color: 'var(--text-primary)' }}>
                {p?.name || id}
                <X size={12} style={{ cursor: 'pointer' }} onClick={() => setCompareIds(ids => ids.filter(x => x !== id))} />
              </span>
            );
          })}
        </div>
      )}

      {!showComparison && player && (
        <PlayerDetailPanel player={player} period={period} onPeriodChange={setPeriod} />
      )}

      {showComparison && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['week', 'Week'], ['28day', '28-Day'], ['preseason', 'Pre-season'], ['season', 'Season']].map(([v, label]) => (
                <button key={v} onClick={() => setPeriod(v)}
                  style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${period === v ? '#2563eb' : 'var(--border-strong)'}`, background: period === v ? '#2563eb' : 'transparent', color: period === v ? '#fff' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ height: 400, maxWidth: 560, margin: '0 auto' }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={comparisonRadarData} outerRadius="70%">
                <PolarGrid stroke="var(--border-strong)" />
                <PolarAngleAxis dataKey="pillar" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
                <PolarRadiusAxis domain={[0, maxRadarValue]} tick={false} axisLine={false} />
                {comparisonPlayers.map((p, i) => (
                  <Radar key={p.id} name={p.name} dataKey={p.name} stroke={PLAYER_COLORS[i % PLAYER_COLORS.length]} fill={PLAYER_COLORS[i % PLAYER_COLORS.length]} fillOpacity={0.15} strokeWidth={2} />
                ))}
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-dimmed)', marginTop: -8 }}>
            Axes with no data plot at zero on the graph.
          </p>

          <div style={{ overflowX: 'auto', marginTop: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg-app)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, color: 'var(--text-muted)' }}>Player</th>
                  {PILLAR_KEYS.map(k => <th key={k} style={{ textAlign: 'right', padding: '8px 12px', fontSize: 11, color: 'var(--text-muted)' }}>{PILLAR_LABELS[k]}</th>)}
                </tr>
              </thead>
              <tbody>
                {comparisonPlayers.map((p, i) => (
                  <tr key={p.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 600, color: PLAYER_COLORS[i % PLAYER_COLORS.length] }}>{p.name}</td>
                    {PILLAR_KEYS.map(k => (
                      <td key={k} style={{ padding: '8px 12px', textAlign: 'right', color: p.pillars[k] == null ? 'var(--text-dimmed)' : 'var(--text-primary)' }}>
                        {p.pillars[k] == null ? 'no data' : Math.round(p.pillars[k])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
    </PageChrome>
  );
}
